export const FEATURE_PERMISSIONS = {
  dashboard: [
    'reports.dashboards.clinical',
    'reports.dashboards.operations',
    'reports.dashboards.finance',
    'reports.dashboards.inventory',
    'patients.view',
    'admissions.view',
    'beds.view',
    'alerts.view',
    'medical-devices.observations.view',
    'medical-devices.status.view',
  ],
  monitoring: ['medical-devices.observations.view'],
  patientMonitor: ['medical-devices.observations.view'],
  patients: ['patients.view'],
  patientRegistration: ['patients.create'],
  // The capability boundary for the action itself, and only that. Listing
  // branches and departments additionally needs `organization.view`, which is a
  // separate grant — the screen names that gap when it is missing rather than
  // hiding a route the server would allow.
  admissionCreate: ['admissions.create'],
  // The admission's one invoice (2026-09-29). Reading it is the invoice grant;
  // changing it is checked on the page against the server's own answer.
  admissionInvoice: ['invoices.view'],
  admissions: ['admissions.view'],
  // «الأطفال في المركز» (2026-10-07): who is in now, each with the invoice to
  // this hour, so the server asks for both grants, and so does the screen.
  admissionsInCenter: ['admissions.view', 'invoices.view'],
  // «فواتير الدخول» (2026-10-14): every admission with its invoice, the closed
  // ones too; the server asks for both grants, as for «الأطفال في المركز».
  admissionInvoiceList: ['admissions.view', 'invoices.view'],
  // The narrow porter grant, deliberately not `beds.status.manage`: the queue
  // exists for the one transition that grant would be an over-grant for.
  housekeeping: ['beds.housekeeping.complete'],
  bedBoard: ['admissions.view', 'beds.view'],
  // The encounter workspace. `clinical.encounters.view` alone opens the
  // worklist and the record; every panel inside gates its own actions on its
  // own grant, because recording an allergy, resolving a problem and closing
  // an encounter are separately delegated in a real department.
  clinicalEncounters: ['clinical.encounters.view'],
  // The note record opens on `view` alone. Every lifecycle action inside it
  // gates on its own grant, because drafting, signing and amending are
  // separately delegated: a scribe drafts, a clinician signs, a consultant
  // amends.
  clinicalNotes: ['clinical.notes.view'],
  nursingAssessments: ['nursing.assessments.view'],
  // The care-plan record. `view` opens the list and the detail; amending and
  // closing need `nursing.care-plans.manage`, and the server additionally
  // refuses a record outside the reader's ward scope with a 403 the screen
  // renders apart from a state conflict.
  nursingCarePlans: ['nursing.care-plans.view'],
  nursingHandovers: ['nursing.handovers.view'],
  nursingEscalations: ['nursing.escalations.view'],
  // Opening an encounter is the manage grant alone. The branch and
  // department pickers read the actor's own locations rather than the
  // organization endpoints, so this screen adds no `organization.view`
  // coupling — the way the admission screen has to.
  clinicalEncounterCreate: ['clinical.encounters.manage'],
  // History is a second boundary inside an encounter: the record may be read
  // without granting access to its immutable audit trail.
  clinicalHistory: ['clinical.encounters.view', 'clinical.history.view'],
  // Patient 360 opens on the patient lookup grant alone. Which parts of the
  // record come back is decided per section by the server, from each
  // section's own clinical grant — adding those here would refuse the whole
  // screen to a caller entitled to some of it.
  patientSummary: ['patients.view'],
  // Its own grant. Reading the record on a screen inside the department
  // and producing a page that leaves the building are different acts.
  printableSummary: ['clinical.encounters.print'],
  // The eMAR workspace opens on the read grant alone. Scanning and recording
  // are separate grants checked inside the screen, so a reader who may see the
  // record is not shown controls that would only be refused.
  emar: ['emar.view'],
  printing: [
    'printing.patient',
    'printing.laboratory',
    'printing.pharmacy',
    'printing.inventory',
    'printing.billing',
  ],
  // The formulary. `view` opens the list and the record; adding or amending an
  // entry needs `pharmacy.catalog.manage`, and mapping stock to a medication is
  // a third grant again — a pharmacist may curate the formulary without being
  // the person who decides which stock item it dispenses from.
  // The two diagnostic catalogues. Reading one is a shift-long activity — a
  // technologist checks a specimen type or a room's duration constantly — while
  // editing either is a curation grant held by far fewer people, so `view`
  // opens the screen and `manage` is checked again before the create and edit
  // controls are rendered at all.
  laboratoryCatalogue: ['lab.catalog.view'],
  radiologyCatalogue: ['rad.catalog.view'],
  // The two queues. `orders.view` opens them; what a reader then sees is
  // narrowed again by the server to the branch and departments they belong to,
  // which is a scope rather than a permission and cannot be decided here.
  laboratoryWorklist: ['lab.orders.view'],
  radiologyWorklist: ['rad.orders.view'],
  // The critical queue opens on *either* view grant, and shows only the family
  // the reader holds. Answering is two further grants checked per section: a
  // clinician may be trusted to answer a laboratory value and not an imaging
  // finding, and the server enforces exactly that split.
  criticalAlerts: ['lab.critical.view', 'rad.critical.view'],
  pharmacyCatalogue: ['pharmacy.catalog.view'],
  catalogItemCreate: ['inventory.items.manage'],
  // Every stock item that is not a medication, and one page per item. Reading
  // is the item grant; amending the item and setting its sale price are checked
  // per section, because a storekeeper may keep items without pricing them.
  consumables: ['inventory.items.view'],
  // Medications and consumables in one list, each half from its own catalogue
  // under its own grant: either grant opens the list with the half it allows.
  itemsCatalogue: ['inventory.items.view', 'pharmacy.catalog.view'],
  // The maker-checker queue. `view` opens it; the decision itself needs
  // `pharmacy.orders.review`, and the server additionally refuses a reviewer
  // who is the prescriber — an authorization refusal the screen renders apart
  // from a state conflict, because the two have different answers.
  pharmacyOrders: ['pharmacy.orders.view'],
  // Dispensing. `view` opens the queue and the record; check, issue, cancel and
  // return are four separate grants, because a bench splits them — and the
  // server additionally refuses a checker who prepared the dispense.
  pharmacyDispenses: ['pharmacy.dispenses.view'],
  // The immutable ledger. Read-only by construction — there is no write to
  // gate, and a correction is a new entry made by the workflow that caused it.
  inventoryLedger: ['inventory.movements.view'],
  // «جرد المخزن» (2026-10-19): every count kept, with what happened in it. Counting, approving and
  // the values at cost and at the sale price are each a grant of their own, checked on the page.
  inventoryStockCount: ['inventory.counts.view'],
  inventoryReorderLevels: ['inventory.locations.view', 'inventory.items.view', 'inventory.stock.view', 'inventory.stock.manage'],
  // «دمج الأصناف المتكررة» (2026-10-23): the managers.
  inventoryMergeItems: ['inventory.items.view', 'inventory.items.merge'],
  inventorySalePrices: [
    'inventory.locations.view',
    'inventory.items.view',
    'inventory.sale-prices.view',
    'inventory.sale-prices.manage',
  ],
  inventoryPackaging: ['inventory.items.view'],
  // The two maker-checker queues on one bench. Viewing needs both, because a
  // screen showing one queue and silently hiding the other is worse than
  // showing neither.
  inventoryApprovals: ['inventory.adjustments.view', 'inventory.transfers.view'],
  // Purchase orders. `view` opens the register and the record; raising,
  // submitting, approving and cancelling are four separate grants, because a
  // purchase order commits money — and the server additionally refuses an
  // approver who is the submitter.
  purchaseOrders: ['purchasing.orders.view'],
  purchaseInvoices: ['purchasing.invoices.view'],
  // Goods receipts. `view` opens the register; posting and reversing are
  // separate grants, and receiving at a cost other than the approved one needs
  // `purchasing.receipts.cost-variance` on top — a refusal the screen renders
  // as a question of authority rather than as a validation error.
  goodsReceipts: ['purchasing.receipts.view'],
  appointments: ['appointments.view', 'clinic-queues.view'],
  // Reading the coverage register is the only grant the screen opens on.
  // Registering a card, chasing a payer and reading the membership number are
  // each checked again per control.
  insuranceCoverages: ['insurance.coverages.view'],
  // Each of the three registers opens on its own read grant. They are separate
  // grants because the work is separate: the desk that raises an authorisation
  // request is not the desk that records what a payer paid.
  insurancePreauthorizations: ['insurance.preauthorizations.view'],
  insuranceClaims: ['insurance.claims.view'],
  insuranceRemittances: ['insurance.remittances.view'],
  patientFlow: ['referrals.view', 'waiting-lists.view', 'appointment-reminders.view'],
  alerts: ['alerts.view'],
  // The operational dashboards open on any one of the four grants, because
  // each section is a separate request behind its own permission and a reader
  // who holds one dashboard should reach the screen that shows it. The other
  // sections then say they are not permitted rather than being requested.
  dashboards: [
    'reports.dashboards.clinical',
    'reports.dashboards.operations',
    'reports.dashboards.finance',
    'reports.dashboards.inventory',
  ],
  alertWorklist: ['alerts.view'],
  notificationInbox: ['notifications.view'],
  // The same read grant as the inbox: preferences decide how the notifications
  // a reader already receives are delivered, never which ones they may see.
  notificationPreferences: ['notifications.view'],
  orders: ['orders.view'],
  // Billing is a capability workspace, not the finance-report dashboard. A
  // read-only auditor may enter through either catalogue or invoice access;
  // create, issue, cancel and override controls gate themselves independently.
  finance: [
    'services.view',
    'services.create',
    'services.manage',
    'invoices.view',
    'invoices.create',
  ],
  accommodationRates: ['accommodation-rates.view'],
  financeDashboard: ['reports.dashboards.finance'],
  // «التقارير» (2026-10-18): the tab opens for whoever can read one of its reports.
  reports: [
    'reports.profit.view', 'reports.stock-value.view', 'reports.dashboards.finance', 'cash-tills.view', 'staff.pay.view',
    'staff.nursing.view', 'staff.view', 'invoices.view', 'external-providers.view', 'reports.exports.view',
    'expense-categories.view', 'suppliers.view', 'suppliers.manage', 'expenses.view', 'expenses.create',
    'reports.money.view', 'reports.referrals.view', 'reports.operations.view', 'reports.attending.view', 'reports.low-stock.view',
    'reports.activity.view', 'admissions.referring-doctors.manage', 'reports.purchases.view', 'reports.attendance.view',
    'inventory.counts.values',
  ],
  caseProfitReport: ['reports.profit.view'],
  // The rest of «التقارير» (2026-10-20).
  moneyReports: ['reports.money.view'],
  referralReports: ['reports.referrals.view'],
  operationsReports: ['reports.operations.view'],
  attendingCasesReport: ['reports.attending.view'],
  lowStockReport: ['reports.low-stock.view'],
  userActivityReport: ['reports.activity.view'],
  // The store's and the staff's (2026-10-20).
  storeReports: ['reports.low-stock.view'],
  purchasesReport: ['reports.purchases.view'],
  countResultsReport: ['inventory.counts.values'],
  nurseSharesReport: ['staff.pay.view'],
  attendanceChangesReport: ['reports.attendance.view'],
  // «الأطباء المحوّلين»: reception adds to the list; the manager sets what each is owed.
  referringDoctors: ['admissions.create', 'admissions.referring-doctors.manage'],
  daySummaryReport: ['reports.profit.view'],
  // The month's staff cost is the whole staff's pay, read with the pay's own grant.
  monthProfitReport: ['reports.profit.view', 'staff.pay.view'],
  stockValueReport: ['reports.stock-value.view'],
  ownerTreasuryDashboard: ['cash-tills.view', 'capital.view'],
  cashTills: ['cash-tills.view'],
  // Expense operators may enter through any one of the separately delegated
  // catalogue, supplier or transaction grants. Each control inside the
  // workspace still checks its exact permission, including maker/checker.
  expenses: [
    'expense-categories.view',
    'suppliers.view',
    'suppliers.manage',
    'expenses.view',
    'expenses.create',
  ],
  // «المعامل ومراكز الأشعة» (2026-10-12): the price lists and what each is
  // owed open on the read grant; keeping the lists and paying from a till are
  // checked again on the page, against `external-providers.manage` and the
  // till's own grants.
  externalProviders: ['external-providers.view'],
  partners: ['partners.view', 'capital.view', 'ownership.view'],
  profitShares: ['profit-distributions.view'],
  partnerPortal: ['profit-distributions.view-own'],
  // The ownership register opens on the read grant alone. Proposing a split
  // needs `ownership.manage` and deciding on one needs `ownership.approve`, and
  // both are checked again per control — a finance clerk who may read the
  // register is not thereby able to change who owns the hospital.
  ownershipRegister: ['ownership.view'],
  // Each opens on its own read grant. Writing a period needs
  // `accounting-periods.manage`; proposing a fee rule needs
  // `management-fees.manage` and deciding on one `management-fees.approve`, and
  // each control checks its own grant inside the screen. Locking a period and
  // calculating profit are separate screens under separate grants.
  accountingPeriods: ['accounting-periods.view'],
  managementFeeRules: ['management-fees.view'],
  // The profit workflow opens on the two read grants it actually renders.
  // Calculating, reviewing, approving, reversing, locking and reopening are
  // each checked again per control, because the whole point of the screen is
  // that no one person may carry a figure from calculation to a locked period.
  profitWorkflow: ['accounting-periods.view', 'profit-calculations.view'],
  reportExports: ['reports.exports.view'],
  profit: [
    'accounting-periods.view',
    'management-fees.view',
    'profit-calculations.view',
    'profit-distributions.view',
    'profit-reports.view',
  ],
  administrationUsers: ['users.view'],
  administrationRoles: ['roles.view'],
  administrationOrganization: ['organization.view'],
  // «إعدادات النظام» (2026-10-11): the hospital's settings, for whoever manages the organisation.
  administrationSettings: ['organization.manage'],
  administrationFacilities: ['beds.view'],
  admissionShiftRoster: ['admissions.shift-roster.manage'],
  // The staff by name and their attendance (2026-09-29).
  staffDirectory: ['staff.view'],
  staffAttendance: ['staff.view'],
  // Each person's attendance over a period, without the money (2026-10-04).
  // Since 2026-10-15 the nursing supervisor reads them too, the nurses only.
  staffAttendanceLog: ['staff.view', 'staff.nursing.view'],
  staffPayroll: ['staff.pay.view', 'staff.nursing.view'],
  // «حضوري ومرتبي» (2026-10-15): a nurse's own month and pay, from her own account.
  myAttendance: ['staff.self'],
  // The consultants' rounds (2026-10-04): kept by whoever writes the staff in and out.
  consultantRounds: ['staff.attendance.record'],
  // The doctors' work as the paper has it (2026-10-05): read with the pay,
  // kept by whoever sets it.
  doctorWorkSheet: ['staff.pay.view'],
  // «الكشوفات» (2026-10-05): reception takes the examination's price into a
  // till, for a child it finds in the registry.
  examinations: ['payments.create', 'patients.view'],
  // Deliberately `audit.view-all`, not `audit.view`: the worklist is the whole
  // organization's trail, and the narrower grant does not reach across it.
  servicePricing: ['services.view'],
  administrationAudit: ['audit.view-all'],
  administrationAuditScoped: ['audit.view'],
  // Watching an interface is an operations job. Reading what a clinical
  // message contained is not, so `interfaces.messages.view-payload` is
  // deliberately absent from this route's requirement: the monitor never calls
  // the payload route, and an operator who holds only these two grants can do
  // the whole job.
  interfaceMonitor: ['interfaces.view', 'interfaces.messages.view'],
  // One grant, and deliberately the narrowest one. `storage.objects.download`
  // is absent because the register never asks for bytes — that separation is
  // the reason a records officer can be given this screen at all — and
  // `storage.objects.view-access-log` is absent because the disclosure history
  // is one section of the detail, not the price of entry.
  objectCustody: ['storage.objects.view'],
} as const satisfies Record<string, readonly string[]>;

export const FEATURE_PERMISSION_MODES = {
  dashboard: 'all',
  monitoring: 'any',
  patientMonitor: 'any',
  patients: 'any',
  patientRegistration: 'any',
  admissionCreate: 'any',
  admissionInvoice: 'any',
  admissions: 'any',
  admissionsInCenter: 'all',
  admissionInvoiceList: 'all',
  clinicalEncounters: 'any',
  clinicalNotes: 'any',
  nursingAssessments: 'any',
  nursingCarePlans: 'any',
  nursingHandovers: 'any',
  nursingEscalations: 'any',
  clinicalEncounterCreate: 'any',
  clinicalHistory: 'all',
  patientSummary: 'all',
  printableSummary: 'all',
  emar: 'any',
  printing: 'any',
  housekeeping: 'any',
  // Both: the board is assembled from the admission list and the bed list, and
  // either alone renders a board that silently lies about the other half.
  bedBoard: 'all',
  laboratoryCatalogue: 'any',
  radiologyCatalogue: 'any',
  laboratoryWorklist: 'any',
  radiologyWorklist: 'any',
  criticalAlerts: 'any',
  pharmacyCatalogue: 'any',
  catalogItemCreate: 'any',
  consumables: 'any',
  itemsCatalogue: 'any',
  pharmacyOrders: 'any',
  pharmacyDispenses: 'any',
  inventoryLedger: 'any',
  inventoryStockCount: 'all',
  inventoryReorderLevels: 'all',
  inventoryMergeItems: 'all',
  inventorySalePrices: 'all',
  inventoryPackaging: 'any',
  inventoryApprovals: 'all',
  purchaseOrders: 'any',
  purchaseInvoices: 'any',
  goodsReceipts: 'any',
  appointments: 'any',
  insuranceCoverages: 'all',
  insurancePreauthorizations: 'all',
  insuranceClaims: 'all',
  insuranceRemittances: 'all',
  patientFlow: 'any',
  alerts: 'any',
  dashboards: 'any',
  alertWorklist: 'any',
  notificationInbox: 'any',
  notificationPreferences: 'any',
  orders: 'any',
  finance: 'any',
  accommodationRates: 'all',
  financeDashboard: 'all',
  reports: 'any',
  caseProfitReport: 'any',
  moneyReports: 'any',
  referralReports: 'any',
  operationsReports: 'any',
  attendingCasesReport: 'any',
  lowStockReport: 'any',
  userActivityReport: 'any',
  storeReports: 'any',
  purchasesReport: 'any',
  countResultsReport: 'any',
  nurseSharesReport: 'any',
  attendanceChangesReport: 'any',
  referringDoctors: 'any',
  daySummaryReport: 'any',
  monthProfitReport: 'all',
  stockValueReport: 'any',
  ownerTreasuryDashboard: 'all',
  cashTills: 'all',
  expenses: 'any',
  externalProviders: 'all',
  partners: 'all',
  profitShares: 'any',
  partnerPortal: 'any',
  ownershipRegister: 'all',
  accountingPeriods: 'all',
  managementFeeRules: 'all',
  profitWorkflow: 'all',
  reportExports: 'any',
  profit: 'all',
  administrationUsers: 'any',
  administrationRoles: 'any',
  administrationOrganization: 'any',
  administrationSettings: 'any',
  administrationFacilities: 'any',
  admissionShiftRoster: 'all',
  staffDirectory: 'all',
  staffAttendance: 'all',
  staffAttendanceLog: 'any',
  staffPayroll: 'any',
  myAttendance: 'all',
  consultantRounds: 'all',
  doctorWorkSheet: 'all',
  examinations: 'all',
  servicePricing: 'any',
  administrationAudit: 'any',
  administrationAuditScoped: 'any',
  // Both, not either: the registry without the messages is a list of endpoints
  // that never says what they did, and the messages without the registry is a
  // stream with no way to tell which source produced it. Either half alone is a
  // screen that misleads.
  interfaceMonitor: 'all',
  objectCustody: 'all',
} as const satisfies Record<keyof typeof FEATURE_PERMISSIONS, 'all' | 'any'>;
